import React, {useEffect, useRef, useState} from 'react';
import {Button, TextField, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper} from '@mui/material';
import {Html5Qrcode} from "html5-qrcode";
import useAxiosInstance from "../useAxiosInstance";
import NavBar from "./NavBar";

// Factory station or QA: unlock a unit by scanning the QR code it shows (ServiceUnlock scan flow):
// a station opens Device Testing ("tap Build" on the unit), QA switches USB debugging on (7 taps on
// the versions). The barcode scanner types the code and Enter into the
// field, which always has the focus; nothing is typed on the unit. Log in once per shift.
// Without a scanner: "Scan with camera" reads the QR with the PC's camera (browsers allow the
// camera only on https or localhost, e.g. through an SSH tunnel).
export default function Unlock() {
    const instance = useAxiosInstance();
    const input = useRef(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [log, setLog] = useState([]);

    const refocus = () => setTimeout(() => input.current && input.current.focus(), 0);
    useEffect(refocus, []);

    const [camera, setCamera] = useState(false);
    const reader = useRef(null);

    const stopCamera = () => {
        const r = reader.current;
        reader.current = null;
        setCamera(false);
        if (r) r.stop().catch(() => {}).finally(() => r.clear());
        refocus();
    };

    const startCamera = () => {
        if (!window.isSecureContext) {
            add(false, "The camera needs https or localhost (open the portal through an SSH tunnel)");
            return;
        }
        setCamera(true);
        setTimeout(() => {
            const r = new Html5Qrcode("qr-camera");
            reader.current = r;
            r.start({facingMode: "environment"}, {fps: 10, qrbox: 250},
                (text) => {
                    stopCamera();
                    send(text);
                }, () => {})
                .catch((e) => {
                    add(false, "Camera not available: " + e);
                    stopCamera();
                });
        }, 0);
    };

    useEffect(() => () => reader.current && reader.current.stop().catch(() => {}), []);

    const add = (ok, text) => setLog(l => [{time: new Date().toLocaleTimeString(), ok, text}, ...l].slice(0, 50));

    const approve = (e) => {
        e.preventDefault();
        const scanned = code.trim();
        setCode("");
        send(scanned);
    };

    const send = (scanned) => {
        if (!scanned) return refocus();
        setBusy(true);
        instance.post('ServiceUnlock/Approve', {requestId: scanned})
            .then((data) => add(true, `Unit ${data.deviceId}: ${data.purpose === "adb" ? "USB debugging on" : "Device Testing open"}`))
            .catch((err) => add(false, err?.response?.data?.message || "Not unlocked"))
            .finally(() => {
                setBusy(false);
                refocus();
            });
    };

    return (
        <div>
            <NavBar title="Unlock a unit" loading={busy}/>
            <div className="mx-3">
                <p>On the unit, Settings &gt; Mi Unit: <b>Device Testing</b> (factory station) - tap <b>Build</b>;
                    <b> USB debugging</b> (QA) - tap the OS / APP / HUB versions 7 times. Scan the QR code the unit
                    shows; it unlocks by itself a few seconds later.</p>
                <form onSubmit={approve}>
                    <TextField inputRef={input} value={code} onChange={(e) => setCode(e.target.value)}
                               onBlur={refocus} label="Scan the unit's QR code" fullWidth autoFocus
                               autoComplete="off" disabled={busy}/>
                </form>
                <div className="mt-2">
                    {camera
                        ? <Button variant="outlined" onClick={stopCamera}>Stop camera</Button>
                        : <Button variant="outlined" onClick={startCamera}>Scan with camera</Button>}
                </div>
                <div id="qr-camera" style={{maxWidth: 420, marginTop: 8}}/>
                <TableContainer component={Paper} className="mt-3">
                    <Table size="small">
                        <TableHead>
                            <TableRow><TableCell>Time</TableCell><TableCell>Result</TableCell></TableRow>
                        </TableHead>
                        <TableBody>
                            {log.map((r, i) =>
                                <TableRow key={i}>
                                    <TableCell>{r.time}</TableCell>
                                    <TableCell style={{color: r.ok ? "#1b8a3a" : "#c62828", fontWeight: 600}}>{r.text}</TableCell>
                                </TableRow>)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}
